import { CarIdentifierWithIcon } from '@components/BookingFlow/Common/CarIdentifierWithIcon';
import { SalesItem } from '@components/BookingFlow/Mobile/salesItems/SalesItem';
import { ServiceCategoryCollapseCard } from '@components/BookingFlow/Mobile/salesItems/ServiceCategoryCollapseCard';
import { t } from '@lingui/core/macro';
import type { CarWithSalesItemsForBookingRecord, SalesItemBookingCategoryType } from '@navio/nsdk';
import groupBy from 'lodash/groupBy';
import { BookingStoreCar, SALES_ITEM_BOOKING_FLOW_TYPE } from 'noddi-types';
import { AnimatedList, NoddiInfoBanner } from 'noddi-ui-web';
import { compareLicensePlates, isLockedBookingSalesItem } from 'noddi-util';
import { useBookingActions, useBookingSelectedSalesItemsCars } from 'noddi-web';
import { useEffect } from 'react';

type AddonPickerProps = {
  carWithAddons: CarWithSalesItemsForBookingRecord;
  /** Resolved by the parent from the draft, so this view never has to handle a car it can't find. */
  car: BookingStoreCar;
};

export const CarWithAddons = ({ carWithAddons, car }: AddonPickerProps) => {
  const { licensePlate, salesItems } = carWithAddons;
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const { addSalesItem } = useBookingActions();

  useEffect(() => {
    for (const item of salesItems) {
      if (!isLockedBookingSalesItem(item)) {
        continue;
      }
      const isAlreadySelected = selectedSalesItemsCars.some(
        (selected) =>
          compareLicensePlates(selected.licensePlate, licensePlate) && selected.salesItemId === item.salesItemId
      );
      if (isAlreadySelected) {
        continue;
      }
      addSalesItem({
        licensePlate,
        salesItemId: item.salesItemId,
        type: SALES_ITEM_BOOKING_FLOW_TYPE.ADDON,
        mutuallyExclusiveSalesItemIds: item.mutuallyExclusiveSalesItemIds,
        isAddonsAvailable: item.isAddonsAvailable,
        isWheelPickupRequired: item.isWheelPickupRequired,
        bookingCategoryType: item.bookingCategoryType as SalesItemBookingCategoryType
      });
    }
  }, [addSalesItem, licensePlate, salesItems, selectedSalesItemsCars]);

  const sortedSalesItems = [...salesItems].sort((a, b) => a.uiSortOrder - b.uiSortOrder);

  const bookingCategories = groupBy(sortedSalesItems, 'bookingCategoryType');

  const numberOfCategories = Object.keys(bookingCategories).length;

  // Api returns empty array when no addons are available for car,
  // this will only be rendered when no addons are available for a car in same booking with other cars having addons
  if (!numberOfCategories) {
    return (
      <div className='flex flex-col gap-5'>
        <CarIdentifierWithIcon car={car} />
        <NoddiInfoBanner
          texts={[
            { value: t`Unfortunately, we don't have offer any addons for the services you've selected on this car` }
          ]}
        />
      </div>
    );
  }

  const onlyOneCategory = numberOfCategories === 1;

  const selectedSalesItemsCar = selectedSalesItemsCars.filter((item) =>
    compareLicensePlates(item.licensePlate, licensePlate)
  );

  return (
    <div className='flex flex-col gap-5'>
      <CarIdentifierWithIcon car={car} bookingCategories={Object.keys(bookingCategories)} />

      {onlyOneCategory ? (
        <AnimatedList delayBetweenItems={100}>
          {sortedSalesItems.map((item) => (
            <SalesItem
              key={item.salesItemId}
              type={SALES_ITEM_BOOKING_FLOW_TYPE.ADDON}
              licensePlate={licensePlate}
              salesItem={item}
            />
          ))}
        </AnimatedList>
      ) : (
        <AnimatedList delayBetweenItems={150}>
          {Object.entries(bookingCategories).map(([bookingCategoryType, salesItems]) => {
            const numberOfSelectedSalesItems = selectedSalesItemsCar.filter((item) =>
              salesItems.map((item) => item.salesItemId).includes(item.salesItemId)
            ).length;

            return (
              <ServiceCategoryCollapseCard
                key={bookingCategoryType}
                bookingCategoryType={bookingCategoryType as SalesItemBookingCategoryType}
                salesItemType={SALES_ITEM_BOOKING_FLOW_TYPE.ADDON}
                salesItems={salesItems}
                licensePlate={licensePlate}
                numberOfSelectedSalesItems={numberOfSelectedSalesItems}
                mountAsExpanded
              />
            );
          })}
        </AnimatedList>
      )}
    </div>
  );
};
